import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useFlags } from 'wsm-common-data';
import {
	selectIsTabbedLayout,
	selectIsGridLayout
} from '../../../utilities/layout';
import FooterSkeleton, {
	FooterSkeletonContainerClasses,
	FooterSkeletonHeightParityClasses
} from '../skeleton/FooterSkeleton';
import {
	useFooterLayoutClasses,
	useDeviceType,
	useMyCarsSiteProperties,
	useFooterCTAs
} from '../../../hooks';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import Footer from './Footer';
import MyCars from './MyCars';
import { FOOTER_CTA_TYPES } from '../../../global-constants';

const FooterContainer = ({ shouldFullyRenderCard, vehicle, ...props }) => {
	const isDesktop = useDeviceType();
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));
	const { enableMyCars } = useMyCarsSiteProperties();
	const { displaySaveIconOn } = usePrefs();
	const flags = useFlags();
	// `ws-inv-listing-cls-media-stack-fix` gates all of the US2092262 CLS work, not
	// just the media stack it is named after — the changes are measured and rolled
	// out together.
	const isClsFixEnabled = flags['ws-inv-listing-cls-media-stack-fix'];
	const { isCTARemoved, footerHasCompare } = useFooterCTAs();

	const footerContainerClasses = useFooterLayoutClasses(
		isDesktop,
		isTabbedLayout,
		shouldFullyRenderCard,
		isGridLayout
	);

	// Don't render footer if all removeFooterLinks options are enabled.
	// If footer is not rendered but Save button displays outside the footer, render MyCars component only.
	const possibleFooterCTAs = [FOOTER_CTA_TYPES.DETAILS];
	let shouldRenderMyCars = false;
	if (enableMyCars) {
		possibleFooterCTAs.push(FOOTER_CTA_TYPES.TRACK);
		if (displaySaveIconOn === 'true') {
			shouldRenderMyCars = true;
		} else {
			possibleFooterCTAs.push(FOOTER_CTA_TYPES.SAVE);
		}
	}
	// footerHasCompare already accounts for compare removal
	const hideFooter =
		possibleFooterCTAs.every((cta) => isCTARemoved(cta)) &&
		!footerHasCompare;
	if (hideFooter) {
		return shouldRenderMyCars ? <MyCars vehicle={vehicle} /> : null;
	}

	const dividerClasses = setClassNames(['mx-4', 'my-0']);

	if (!shouldFullyRenderCard) {
		footerContainerClasses.push(
			...(isClsFixEnabled
				? FooterSkeletonHeightParityClasses
				: FooterSkeletonContainerClasses)
		);
	}

	return (
		<>
			<hr className={dividerClasses} />
			<div className={setClassNames(footerContainerClasses)}>
				{shouldFullyRenderCard ? (
					<Footer vehicle={vehicle} {...props} />
				) : (
					<FooterSkeleton />
				)}
			</div>
		</>
	);
};

FooterContainer.propTypes = {
	shouldFullyRenderCard: PropTypes.bool.isRequired,
	vehicle: vehiclePropType.isRequired
};

export default FooterContainer;
